// Grades: one letter for how a creature's rolls compare with fresh wild
// catches. A grade only displays the 0-100 score (ratingOf); no game math
// reads it. The cutoffs come from 1,000,000 simulated catches per stage (see
// scripts/grade-tables.ts): at stage 1 about 53% are C, 39% B, 7.5% A, 0.9% S
// and 0.09% S+.
import { BELOW } from './grade-tables.ts';

export type Grade = 'C' | 'B' | 'A' | 'S' | 'S+';

/** Lowest score of each grade, best first. */
export const GRADE_CUTOFFS: readonly (readonly [Grade, number])[] = [['S+', 45], ['S', 36], ['A', 29], ['B', 22], ['C', 0]];

/** Fill colors (C is an outline in this gray). Gold is kept for auras. */
export const GRADE_COLORS: Record<Grade, string> = { C: '#5d6884', B: '#62c26e', A: '#4ea3ff', S: '#b77cff', 'S+': '#ff9f43' };

/** What each grade means in plain words. */
export const GRADE_MEANING: Record<Grade, string> = {
  C: 'Bottom half', B: 'Better than most', A: 'About 1 in 10', S: 'About 1 in 100', 'S+': 'About 1 in 1,000',
};

export function gradeOf(score: number): Grade {
  for (const [g, min] of GRADE_CUTOFFS) if (score >= min) return g;
  return 'C';
}

/** The `autoSellBelow` score that releases every new catch below this grade. */
export function gradeFloor(g: Grade): number {
  return GRADE_CUTOFFS.find(([x]) => x === g)![1];
}

/** Percent of fresh wild catches of this stage (1-3) that score lower. */
export function percentileBelow(score: number, stage: number): number {
  const t = BELOW[Math.min(3, Math.max(1, stage))];
  return t[Math.min(100, Math.max(0, Math.round(score)))];
}

/** "higher than 97% of wild catches", or "top 0.1% of wild catches" at the very top. */
export function percentileText(score: number, stage: number): string {
  const below = percentileBelow(score, stage);
  if (below >= 99.9) return 'top 0.1% of wild catches';
  if (below >= 99.5) return 'top 0.5% of wild catches';
  if (below >= 99) return 'top 1% of wild catches';
  if (below < 1) return 'lower than most wild catches';
  return `higher than ${Math.floor(below)}% of wild catches`;
}
